import React, {useContext, useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {fetchWithToken} from "../../api/apiBase";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {TableBody, TableCell, TableHead, TableHeader, TableRow} from "../ui/table";
import {Badge} from "../ui/badge";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";


/** one row of email_log, raw from mongo - see server backend/email/email_model.py */
interface EmailLog {
    _id: string;
    triggered_by: string;       // sender's email, or a job name like 'rank_watch'
    sent_to: string;
    message_type: string;
    subject: string | null;
    status: string | null;      // accepted | send_failed | delivered | failed | opened
    status_at: string | null;
    created_at: string;
    error: string | null;
    last_event: { reason?: string, description?: string, message?: string } | null;
}


function formatDateTime(value: string | null) {
    return value ? new Date(value).toLocaleString() : "";
}


/** a person's email, or 'Automatic' for mail a scheduled job sent */
function fromLabel(log: EmailLog) {
    return log.triggered_by?.includes("@") ? log.triggered_by : "Automatic";
}


function StatusBadge({log}: { log: EmailLog }) {
    switch (log.status) {
        case "delivered":
            return <Badge variant="ok">Delivered</Badge>;
        case "opened":
            return <Badge className="border-transparent bg-blue-500 text-white">Opened</Badge>;
        case "failed":
            return <Badge variant="destructive">Failed</Badge>;
        case "send_failed":
            return <Badge variant="destructive">Not sent</Badge>;
        default:
            return <Badge variant="secondary">Sent</Badge>;
    }
}


/** why a failed one failed - mailgun's reason for bounces, our error for rejected sends */
function failureReason(log: EmailLog) {
    if (log.status === "send_failed") return log.error;
    if (log.status === "failed") {
        return log.last_event?.description || log.last_event?.message || log.last_event?.reason;
    }
    return null;
}


const PAGE_SIZE = 50;


/**
 * Developer only - every email the project has sent, newest first, with delivery status.
 * Loads PAGE_SIZE at a time; scrolling near the bottom of the table fetches the next page.
 */
export default function ProjectMessage() {
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<string>();
    const projectContext = useContext(ProjectContext);
    const [rows, setRows] = useState<EmailLog[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);          // first page only
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false); // later pages, shown inline
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);            // first page failed
    const [moreError, setMoreError] = useState<string | null>(null);    // a later page failed
    // state updates are async, so a fast scroll could fire twice before isLoadingMore flips
    const fetchingMore = useRef<boolean>(false);
    // the sidebar switches project without unmounting this page
    const currentProject = useRef(project_id);
    currentProject.current = project_id;

    // first page - and a clean slate whenever the project changes
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        setRows([]);
        setHasMore(true);
        setMoreError(null);
        setIsLoading(true);
        fetchWithToken(`/email/v1/${project_id}/list?skip=0&limit=${PAGE_SIZE}`, accessToken)
            .then((page: EmailLog[]) => {
                if (cancelled) return;
                setRows(page);
                setHasMore(page.length === PAGE_SIZE);
                setError(null);
            }).catch(() => {
                if (!cancelled) setError("Error loading messages :(");
            }).finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [project_id, accessToken]);

    const loadMore = () => {
        if (!accessToken || !hasMore || fetchingMore.current) return;
        const requested = project_id;
        fetchingMore.current = true;
        setIsLoadingMore(true);
        setMoreError(null);
        fetchWithToken(`/email/v1/${project_id}/list?skip=${rows.length}&limit=${PAGE_SIZE}`, accessToken)
            .then((page: EmailLog[]) => {
                if (requested !== currentProject.current) return;
                // new mail at the top shifts the pages down, so this one can repeat a row or two
                setRows((prev) => {
                    const seen = new Set(prev.map((r) => r._id));
                    return prev.concat(page.filter((r) => !seen.has(r._id)));
                });
                setHasMore(page.length === PAGE_SIZE);
            }).catch(() => {
                setMoreError("Couldn't load more messages - scroll to try again");
            }).finally(() => {
                fetchingMore.current = false;
                setIsLoadingMore(false);
            });
    };

    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const box = e.currentTarget;
        if (box.scrollHeight - box.scrollTop - box.clientHeight < 200) loadMore();
    };

    if (projectContext?.role !== CompanyType.developer) return <></>;
    if (isLoading) return <div><BaseLoadingSpinner/></div>;
    if (error) return <div>{error}</div>;

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <h1 className="text-xm font-semibold">Messages</h1>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0
                    ? "No messages sent yet"
                    // page-height box that scrolls; TableHeader is sticky so the column names stay put.
                    // a plain <table> rather than <Table>, whose own overflow wrapper would stop the header sticking
                    : <div className="h-[calc(100vh-250px)] overflow-y-auto" onScroll={onScroll}>
                      <table className="w-full caption-bottom text-sm">
                        <TableHeader className="z-10">
                            <TableRow>
                                <TableHead>Sent</TableHead>
                                <TableHead>From</TableHead>
                                <TableHead>To</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Subject</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((log) => (
                                <TableRow key={log._id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(log.created_at)}</TableCell>
                                    <TableCell>{fromLabel(log)}</TableCell>
                                    <TableCell>{log.sent_to}</TableCell>
                                    <TableCell>{log.message_type.replace(/_/g, " ")}</TableCell>
                                    <TableCell>{log.subject}</TableCell>
                                    <TableCell>
                                        <StatusBadge log={log}/>
                                        {log.status_at && log.status !== "accepted" &&
                                            <div className="text-xs text-gray-400">{formatDateTime(log.status_at)}</div>
                                        }
                                        {failureReason(log) &&
                                            <div className="text-xs text-red-600">{failureReason(log)}</div>
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                            {isLoadingMore &&
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-gray-400">Loading…</TableCell>
                                </TableRow>
                            }
                        </TableBody>
                      </table>
                      {moreError && <div className="p-2 text-xs text-red-600">{moreError}</div>}
                    </div>
                }
            </CardContent>
        </Card>
    );
}
